import * as React from 'react';
import { cn } from '../../lib/utils';
import { ContentContainer } from './ContentContainer';

export interface AppLayoutProps {
  /** The graphite left sidebar — typically a <SharedSidebarShell /> instance. */
  sidebar: React.ReactNode;
  /** The fixed top bar — typically a <PlatformTopbar /> instance. */
  topbar: React.ReactNode;
  /**
   * Optional banner row above the topbar. Reserved for platform-wide
   * announcements (trial status, scheduled maintenance, billing alerts).
   * Renders nothing when omitted, so existing consumers are unaffected.
   */
  banner?: React.ReactNode;
  /** Page content. Usually a router outlet from the consumer app. */
  children: React.ReactNode;
  /** Disable the default ContentContainer wrapper around `children`. */
  rawContent?: boolean;
  /** Extra class names on the outer shell. */
  className?: string;
}

/**
 * AppLayout — the canonical ZAFROI shell. A graphite sidebar on the left, a
 * sticky topbar at the top, and a scrollable content column underneath.
 *
 * Provider stack (router, query client, toaster, theme) belongs *above* this
 * component, in the consumer app's entry point.
 */
export function AppLayout({
  sidebar,
  topbar,
  banner,
  children,
  rawContent,
  className,
}: AppLayoutProps) {
  return (
    <div className={cn('flex h-screen w-full bg-background', className)}>
      {sidebar}
      <div className="flex min-w-0 flex-1 flex-col">
        {banner}
        {topbar}
        <main className="flex-1 overflow-y-auto">
          {rawContent ? children : <ContentContainer>{children}</ContentContainer>}
        </main>
      </div>
    </div>
  );
}
